import {ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

import {validateHostName, validateIpAddress} from '../../../../validators/name-servers-validator.directive';
import {ValidatorService} from '../../../../services/validator.service';
import {ValueFromAdditionalMapPipe} from '../../../../pipes/value-from-additional-map.pipe';
import {ServerConstants} from '../../../../model/ServerConstants';
import {SortIpUtilPipe} from '../sort-ip-util.pipe';
import {ErrorVO} from '../../../../generate/ErrorVO';
import {UtilService} from '../../../../util/util.service';
import {isHostnameUnderTld} from '../../../../util/CommonFunctions';
// TODO(RZ-2562): Replace legacy VO types (NameServerVO) with SDK equivalents (NameServer)
import {NameServerVO} from '../../../../generate/TopLevelDomainVO';
import {NameServer} from '@iana-rzms/bff-sdk';


@Component({
    selector: 'name-servers-edit',
    templateUrl: './name-servers-edit.component.html',
    styleUrls: ['./name-servers-edit.component.scss'],
    standalone: false
})
export class NameserversEditComponent implements OnInit {

  @Input()
  nameServers: Array<NameServerVO>;

  @Input()
  newDelegation: boolean;

  @Input()
  tldName: string;

  blurHostErrors: Map<number, string[]> = new Map();
  private cdr = inject(ChangeDetectorRef);

  editNameServersForm: FormGroup;
  @Output()
  nameServersEventOutPut = new EventEmitter<Array<NameServerVO>>();
  @Output()
  cancelOutPut = new EventEmitter<void>();
  minimumNSRequirement: boolean;
  errors: ErrorVO;
  serverConstants = ServerConstants;

  constructor(private formBuilder: FormBuilder, private util: UtilService, private validator: ValidatorService) {
  }


  ngOnInit() {
    this.editNameServersForm = this.formBuilder.group({
      nameservers: this.initNameServersArray()
    });
  }

  onSubmit(): void {

    const outputNameServers: Array<NameServerVO> = [];
    for (let i in this.editNameServersForm.getRawValue().nameservers) {
      const ns: NameServerVO = {};
      const formVal = this.editNameServersForm.getRawValue().nameservers[i];
      ns.hostname = formVal.host.trim();
      ns.ipAddresses = formVal.ip.split(",").filter((elem, index, self) => {
        return index === self.indexOf(elem)
      }).map(item => item.trim());
      ns.locks = formVal.locks;
      ns.new = formVal.new;
      ns.shared = formVal.shared;
      if (!this.util.checkIfEmpty(ns.hostname) && !this.util.checkIfEmpty(ns.ipAddresses.toString())) {
        outputNameServers.push(ns);
      }
    }

    // TODO: Update validator to accept SDK NameServer type directly instead of 'as any' cast
    this.validator.validateNameServers(outputNameServers as any, this.tldName || undefined).subscribe(data => {
      if (data == null) {
        this.blurHostErrors.clear();
        this.nameServersEventOutPut.emit(outputNameServers);
      } else {
        this.errors = data;

        if (this.formNameServers && this.formNameServers.length < 1) {
          this.minimumNSRequirement = true;
          this.errors = {};
        }
      }
    });
  }

  cancel(): void {
    this.cancelOutPut.emit();
  }

  removeNameServer(index: number) {
    this.formNameServers.removeAt(index);

    if (this.blurHostErrors.size > 0) {
      const updatedBlurHostErrors = new Map<number, string[]>();

      this.blurHostErrors.forEach((value, key) => {
        if (key < index) {
          // Indices before the removed one stay the same.
          updatedBlurHostErrors.set(key, value);
        } else if (key > index) {
          // Shift indices after the removed one down by 1.
          updatedBlurHostErrors.set(key - 1, value);
        }
        // Entry with key === index is dropped.
      });

      this.blurHostErrors = updatedBlurHostErrors;
    }
  }

  addAditionalNS() {
    this.minimumNSRequirement = false;

    this.formNameServers.push(new FormGroup({
      host: new FormControl('', [Validators.required, validateHostName]),
      ip: new FormControl('', [Validators.required, validateIpAddress])
    }));
  }

  checkIfIPError(errors): boolean {
    return errors && errors.invalidIpAddress && errors.invalidIpAddress.length > 0 && !this.util.checkIfEmpty(errors.ipAddress);
  }

  checkIfHostError(errors): boolean {
    return errors && errors.invalidHostName && !this.util.checkIfEmpty(errors.hostName);
  }

  checkIfIPErrorForCSS(c: AbstractControl): string {
    let errors = c.get("ip").errors;
    return this.checkIfIPError(errors) || this.IPValidationError(c.get('host').value, 0).length > 0 ? "input-field-error" : "";
  }

  checkIfHostErrorForCSS(c: AbstractControl, index: number): string {
    let errors = c.get("host").errors;
    return this.checkIfHostError(errors) || this.hostValidationError(c.get('host').value, index).length > 0 || (this.blurHostErrors.get(index)?.length > 0) ? "input-field-error" : "";
  }

  onHostFocus(index: number, formGroup: AbstractControl): void {
    this.blurHostErrors.delete(index);
    formGroup.get('host').markAsUntouched();
  }

  onHostBlur(index: number): void {
    const hostControl = (this.formNameServers.at(index) as FormGroup).get('host');
    const hostname = (hostControl.value as string)?.trim();
    if (!hostname || hostControl.errors) {
      this.blurHostErrors.delete(index);
      return;
    }
    if (this.tldName && isHostnameUnderTld(hostname, this.tldName)) {
      this.blurHostErrors.delete(index);
      return;
    }
    this.validator.validateNameServers([{hostname, ipAddresses: ['0.0.0.0']} as NameServer], this.tldName || undefined).subscribe(data => {
      const currentHostname = ((this.formNameServers.at(index) as FormGroup).get('host').value as string)?.trim();
      if (currentHostname !== hostname) {
        return;
      }
      const errors = this.extractHostErrors(data);
      const next = new Map(this.blurHostErrors);
      if (errors.length > 0) {
        next.set(index, errors);
      } else {
        next.delete(index);
      }
      this.blurHostErrors = next;
      this.cdr.detectChanges();
    });
  }

  private extractHostErrors(errorVO: ErrorVO): string[] {
    return this.util.getFieldErrorMessages('hostname', errorVO);
  }


  hostValidationError(val: string, index: number): string[] {
    let hostErrors: Array<string> = this.getNameServerValidationError(val, "hostname");
    if (index + 1 === this.formNameServers.length) {
      hostErrors = hostErrors.concat(this.getNameServerValidationError("$global$", "hostname"))
    }
    return hostErrors;
  }

  IPValidationError(val: string, index: number): string[] {
    let ipErrors: Array<string> = this.getNameServerValidationError(val, "ipAddress");
    if (index + 1 === this.formNameServers.length) {
      ipErrors = ipErrors.concat(this.getNameServerValidationError("$global$", "ipAddress"))
    }
    return ipErrors;
  }

  getNameServerValidationError(name: string, field: string): string[] {

    let listOfErrors: Array<string> = [];
    let error: ErrorVO = this.util.getFieldError(name, this.errors);
    let hostError: ErrorVO = this.util.getFieldError(field, error);
    if (hostError != null) {
      listOfErrors = hostError.errorMessages;
    }

    return listOfErrors;
  }


  private initNameServersArray() {
    const formArr = new FormArray([]);
    let ns: NameServerVO = {
      ipAddresses: [],
      hostname: '',
      shared: false
    };

    if (!this.nameServers || this.nameServers.length === 0) {
      this.nameServers = [ns, {...ns}];
    }

    this.nameServers.forEach((ns) => {
      // Ensure ipAddresses is always an array
      const ipAddresses = ns.ipAddresses || [];
      let hostIpGroup = this.formBuilder.group({
        host: [{value: ns.hostname, disabled: this.isDisabled(ns, "UPDATE")}, [Validators.required, validateHostName]],
        ip: [{
          value: new SortIpUtilPipe().transform(ipAddresses).join(","),
          disabled: this.isDisabled(ns, "UPDATE")
        }, [Validators.required, validateIpAddress]],
        locks: ns.locks,
        new: ns.new,
        shared: ns.shared ?? false
      });

      formArr.push(hostIpGroup);
    });

    return formArr;
  }

  get formNameServers(): FormArray {
    return this.editNameServersForm.controls['nameservers'] as FormArray;
  }

  isDisabled(ns: NameServerVO, type: string): boolean {
    return !this.util.checkIfEmpty(new ValueFromAdditionalMapPipe().transform(ns, ServerConstants.LOCK_FIELD_NAME, type));
  }
}
